import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Box, Button, CircularProgress, palette, type ButtonProps } from '@munifocus/ui';

/**
 * A button that fires only after being held for `holdMs` — by pointer, or by
 * Space/Enter on the keyboard. Letting go early cancels. For actions that must
 * never happen by accident (an SOS), where a single click is too cheap.
 *
 * The progress ring shows how far along the hold is; `aria-describedby` points
 * at the caller's instruction so a screen-reader user hears "press and hold".
 */
export function HoldButton({
  holdMs,
  onComplete,
  children,
  holdingLabel,
  describedBy,
  ...props
}: {
  holdMs: number;
  onComplete: () => void;
  children: ReactNode;
  holdingLabel: string;
  describedBy?: string;
} & Omit<ButtonProps, 'onClick' | 'onComplete'>) {
  const [progress, setProgress] = useState(0);
  const started = useRef<number | null>(null);
  const tick = useRef<ReturnType<typeof setInterval> | null>(null);

  const stop = useCallback(() => {
    if (tick.current) clearInterval(tick.current);
    tick.current = null;
    started.current = null;
    setProgress(0);
  }, []);

  const start = useCallback(() => {
    if (started.current !== null) return;
    started.current = Date.now();
    tick.current = setInterval(() => {
      const at = started.current;
      if (at === null) return;
      const ratio = Math.min(1, (Date.now() - at) / holdMs);
      setProgress(ratio);
      if (ratio >= 1) {
        stop();
        onComplete();
      }
    }, 50);
  }, [holdMs, onComplete, stop]);

  useEffect(() => stop, [stop]);

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
      e.preventDefault();
      start();
    }
  };
  const onKeyUp = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      stop();
    }
  };

  const holding = progress > 0;
  return (
    <Button
      {...props}
      aria-describedby={describedBy}
      onPointerDown={(e) => {
        e.preventDefault();
        start();
      }}
      onPointerUp={stop}
      onPointerLeave={stop}
      onPointerCancel={stop}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      onBlur={stop}
      // A click alone does nothing; only the completed hold acts.
      onClick={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
      startIcon={
        holding ? (
          <Box sx={{ display: 'inline-flex', color: palette.background.paper }}>
            <CircularProgress
              variant="determinate"
              value={progress * 100}
              size={16}
              thickness={6}
              color="inherit"
              aria-hidden
            />
          </Box>
        ) : (
          props.startIcon
        )
      }
    >
      {holding ? holdingLabel : children}
    </Button>
  );
}
